import { createElement, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faRotate, faWarning } from "@fortawesome/pro-regular-svg-icons";

import { applicationConfig } from "../../../../environmentConfiguration";
import { translationKeys } from "../../../models/app-translation-keys";
import { ActionButton } from "../../Shared/Buttons/ActionButton/ActionButton";
import { sharedDialog } from "../../Shared/Components/Dialogs/SharedDialog";
import { Icon } from "../../Shared/Icon/Icon";
import { BugReportDialogContent } from "../UserProfile/BugReportDialog/BugReportDialogContent/BugReportDialogContent";
import { bugReportDialogFooterActions } from "../UserProfile/BugReportDialog/bugReportDialogFooter";
import { IBugReportDialogState } from "../UserProfile/BugReportDialog/bugReportDialogTypes";

import "./GlobalErrorBoundary.scss";

interface IGlobalErrorFallbackProps {
  error?: Error | null;
}

export const GlobalErrorFallback: FunctionComponent<IGlobalErrorFallbackProps> = ({ error }) => {
  const { t } = useTranslation();

  const handleReload = (): void => {
    window.location.reload();
  };

  const handleGoHome = (): void => {
    window.location.href = "/";
  };

  const handleReportBug = (): void => {
    const stackTrace = `Debug infos for developers: \n\n${error?.stack ?? ""}`;

    sharedDialog.openSharedDialog({
      title: t(translationKeys.bugReport.dialogTitle),
      size: "small",
      onClose: () => {},
      sharedState: {
        isValid: false,
        formData: { summary: "", steps: "", actual: "", moreInfo: stackTrace },
      } satisfies IBugReportDialogState,
      customContent: createElement(BugReportDialogContent),
      footerActions: bugReportDialogFooterActions({ onHide: () => {} }),
      className: "bug-report-dialog",
      customDismissBehavior: { closeIcon: true, escape: true, outsideClick: false },
    });
  };

  const showReportABug = applicationConfig.featureFlags.enableReportABug;

  return (
    <div className="error-boundary">
      <div className="content">
        <span className="icon">
          <Icon icon={faWarning} iconSize="5xl" color="warning" />
        </span>

        <h1 className="title">{t(translationKeys.globalErrorBoundary.title)}</h1>

        <p className="subtitle">{t(translationKeys.globalErrorBoundary.subtitleText)}</p>

        <div className="actions">
          <ActionButton
            id="global-error-fallback-reload-button"
            label={t(translationKeys.globalErrorBoundary.actions.reloadPage)}
            icon={faRotate}
            onClick={handleReload}
            showBackground
          />

          <ActionButton
            id="global-error-fallback-go-home-button"
            label={t(translationKeys.globalErrorBoundary.actions.goToHome)}
            onClick={handleGoHome}
          />

          <ActionButton
            id="global-error-fallback-report-bug-button"
            hide={!showReportABug}
            label={t(translationKeys.bugReport.menuItemLabel)}
            onClick={handleReportBug}
          />
        </div>
      </div>
    </div>
  );
};
